/*** IMPORTS FROM imports-loader ***/
import hydrator from "@gjensidige/views-api/src/runtime/hydrator";
import Component from ".";

/* global hydrator, Component */

// create a map of what we have hydrated, share it on window
window.__HYDRATED__ = window.__HYDRATED__ || {};

// get the current script executing
const partSrc = window.document?.currentScript?.getAttribute('src');

// filter the ones that need to be hydrated (data-nodeid contains the id for the props -> only hydration on node id's)
const parts = Array.from(
  document.querySelectorAll('script[src="' + partSrc + '"]')
).filter((part) => part.getAttribute('data-nodeid'));

// if this selection of parts contains elements that needs to be hydrated, add an eventListener
if (parts.length && window.addEventListener) {
  window.addEventListener('load', function () {
    parts.forEach((part) => {
      // make sure we only hydrate our components once
      const NODE_ID = part.getAttribute('data-nodeid');
      if (!window.__HYDRATED__[NODE_ID]) {
        window.__HYDRATED__[NODE_ID] = true;
        hydrator(Component, NODE_ID);
      }
    });
  });
}


import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types/prop-types';
import superagent from 'superagent';
import { Flex, Link, Text } from '@gjensidige/builders-components';
import {
  Email,
  Facebook,
  Instagram,
  Linkedin,
  Phone,
  PhoneCall,
  Twitter
} from '@gjensidige/builders-icons';

import { Nav } from './nav';
import { FooterProps as Props, OptionalFooterApiUrl } from './types';

import '../less/footer.less';

export const Footer = ({
  footerData,
  className,
  ...rest
}: OptionalFooterApiUrl & JSX.IntrinsicElements['footer']) => {
  const {
    cookiePreferencesLabel,
    copyrightLabel,
    links,
    nav,
    homeUrl,
    social,
    contact
  } = footerData as Props;

  const [currentPath, setCurrentPath] = useState(homeUrl);

  useEffect(() => {
    if (window && window.location && window.location.pathname) {
      setCurrentPath(window.location.pathname);
    }
  }, []);

  return (
    <footer
      className={classNames('builders_global_footer', className)}
      {...rest}
    >
      <Flex center layout="1" align="start">
        <div className="builders_global_topSection">
          {!!(nav?.global || nav?.local?.length) && (
            <Nav currentPath={currentPath} nav={nav} />
          )}
          <div className="builders_global_social">
            <Social icon={<Phone />} social={contact?.phone} protocol="tel:" />
            <Social icon={<PhoneCall />} social={contact?.callMe} />
            <Social
              icon={<Email />}
              protocol="mailto:"
              social={contact?.email}
            />
            <Social social={social?.instagram} icon={<Instagram />} />
            <Social social={social?.linkedin} icon={<Linkedin />} />
            <Social social={social?.facebook} icon={<Facebook />} />
            <Social social={social?.twitter} icon={<Twitter />} />
          </div>
        </div>

        <div className="builders_global_bottomSection">
          <div className="builders_global_links">
            {!!cookiePreferencesLabel && (
              <a href="#cookie:settings">{cookiePreferencesLabel}</a>
            )}
            {links?.map(({ text, url, newWindow }, index) => (
              <Link
                key={index}
                href={url}
                target={newWindow ? '_blank' : undefined}
              >
                {text || url}
              </Link>
            ))}
          </div>
          {!!copyrightLabel && (
            <div className="builders_global_copyright">
              <Text size="small">{copyrightLabel}</Text>
            </div>
          )}
        </div>
      </Flex>
    </footer>
  );
};

Footer.CSR = true;
Footer.propTypes = {
  footerApiUrl: PropTypes.string,
  footerData: PropTypes.object
};

Footer.getServerSideProps = async function ({ footerApiUrl }) {
  if (!footerApiUrl) {
    console.error(
      `missing required footerApiUrl prop set by FOOTER_API_URL env var`
    );
  }

  try {
    const res = await superagent
      .get(`${footerApiUrl}`)
      .accept('application/json')
      .retry()
      .send();

    return { footerData: res.body };
  } catch (e) {
    console.log('could not fetch footer data because of: ', e);
  }
};

export default Footer;

const Social = ({ social, icon, protocol = '' }) =>
  !!social && (
    <a
      href={protocol ? `${protocol}${social.target}` : social.url}
      target={social.newWindow ? '_blank' : undefined}
      rel="noreferrer"
    >
      {icon}
      {social.label}
    </a>
  );
